import { FC, Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Checkbox, DefaultButton, Stack, Text } from "@fluentui/react";
import { GroupBox, ICheckbox, ITextField, ITheme, Separator } from "cayo.ui";
import DOMPurify from "dompurify";
import styled from "styled-components";
import GenericScheme from "@cayo/core/components/Schemes/GenericScheme";
import WizardsSchemeHolder from "@cayo/core/components/Schemes/WizardScheme/WizardsSchemeHolder";
import { AuthMode } from "@cayo/shared/api/types/sso";
import { CenteredPanel } from "@cayo/shared/components/CommonLayout";
import useService from "@cayo/shared/services/hooks/useService";
import { ILoginScheme } from "../../api/schema.api";
import SSO from "../../api/sso";
import { convertInlineStylesToClasses } from "../../utils/html-utils";
import Form from "../Form";
import { renderButtonContent } from "../Form/renderers/renderers.buttons";
import useTypeAnnotations from "../GlobalHooks/type-annotations.hook";

const LoginScheme: FC<ILoginScheme> = (props) => {
  const { background, passwordFlow, title, sso, copyrightText, disclaimer, extendedContent } =
    props;
  const protocol = window.location.protocol;
  const formRef = useRef<{ getModel: () => Record<string, any> }>(null);
  const [isinitialized, setIsInitialized] = useState(false);
  const [acceptToLogin, setAcceptToLogin] = useState<boolean>(false);
  const [passwordConfig, setPasswordConfig] = useState(passwordFlow);

  const isSSOSupported = !!sso?.length && protocol !== "http:";
  const actionBuilder = useService("actionBuilder");
  const { getIconAnnotation } = useTypeAnnotations();

  const sanitizedDisclaimerText = useMemo(() => {
    if (typeof disclaimer?.text !== "string" || disclaimer?.text?.trim() === "") {
      return "";
    }

    const purified = DOMPurify.sanitize(disclaimer?.text);

    const nonce = window["NONCE_ID"] ?? "";
    return convertInlineStylesToClasses(purified, nonce, "auth-disclaimer");
  }, [disclaimer]);

  useEffect(() => {
    if (!passwordFlow) {
      return;
    }

    const formModel = formRef.current?.getModel();

    const updatedConfig = {
      ...passwordFlow,
      items: passwordFlow?.items?.map((item: ITextField | ICheckbox) => {
        if (formModel && formModel[item.name!] !== undefined) {
          item.value = formModel[item.name!];
        }

        if (item.name === "submit") {
          return {
            ...item,
            disabled: disclaimer?.acknowledgmentRequired ? !acceptToLogin : false,
          };
        }
        return item;
      }),
    };

    setPasswordConfig(updatedConfig);
  }, [disclaimer?.acknowledgmentRequired, acceptToLogin, passwordFlow]);

  useEffect(() => {
    setIsInitialized(true);
  }, []);

  if (!isinitialized) {
    return <Fragment />;
  }

  return (
    <BackgroundImage src={background!}>
      <StyledCenteredPanel dropShadow={true}>
        <GroupBox title={title!}>
          {passwordConfig && (
            <Form
              actionBuilder={actionBuilder}
              {...passwordConfig!}
              className="static-form"
              getIconAnnotation={getIconAnnotation}
              isMessageBarMultiline={true}
              ref={formRef}
            ></Form>
          )}
          {isSSOSupported && (
            <>
              <Stack tokens={{ childrenGap: 10 }}>
                {props.passwordFlow && isSSOSupported && <Separator title="or" preset="small" />}
                <Stack horizontal={false} tokens={{ childrenGap: 10 }}>
                  {sso?.map((s) => (
                    <DefaultButton
                      key={s.scheme}
                      disabled={disclaimer?.acknowledgmentRequired ? !acceptToLogin : false}
                      onClick={() => SSO.useExternalAuth(s.scheme as AuthMode)}
                    >
                      {renderButtonContent(s?.title, s?.image)}
                    </DefaultButton>
                  ))}
                </Stack>
              </Stack>
            </>
          )}
          {process.env.NODE_ENV === "development" && extendedContent && (
            <>
              <Separator title="or" />
              <GenericScheme {...extendedContent} />
              <WizardsSchemeHolder onWizardAction={() => {}} />
            </>
          )}
        </GroupBox>
        {sanitizedDisclaimerText && (
          <Disclaimer dangerouslySetInnerHTML={{ __html: sanitizedDisclaimerText }} />
        )}
        {disclaimer?.acknowledgmentRequired && (
          <AcknowledgmentCheckbox
            checked={acceptToLogin}
            id="acknowledgment"
            label={disclaimer?.acknowledgmentText}
            onChange={(_, checked) => setAcceptToLogin(!!checked)}
          />
        )}
        {copyrightText && <StyledCopyrightText variant="tiny">{copyrightText}</StyledCopyrightText>}
      </StyledCenteredPanel>
    </BackgroundImage>
  );
};

const AcknowledgmentCheckbox = styled(Checkbox)`
  width: 100%;
  padding: 10px 0;
`;

const Disclaimer = styled.div`
  width: 100%;
  max-height: 250px;
  overflow-y: auto;
  margin: 10px 0;
`;

const StyledCopyrightText = styled(Text)`
  width: 100%;
  margin-top: 4px;
`;

const StyledCenteredPanel = styled(CenteredPanel)`
  width: 450px;
  height: auto;
  background-color: ${(props) => (props.theme as ITheme).cayoTheme.brandColors.primaryBackground};
  padding: 1em 1.4em;
  @media (max-width: 576px) {
    max-width: 500px;
  }
`;

const BackgroundImage = styled.div<{ src: string }>`
  background: ${(props) => `url('${props.src}') no-repeat fixed center`};
  display: flex;
  height: 100vh;
  align-items: center;
  justify-content: center;
  background-size: cover;
`;

export default LoginScheme;
